import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const ManualQuizCreator = ({ onQuestionsCreated, initialQuestions = [] }) => {
  // Define createEmptyQuestion before using it
  function createEmptyQuestion(number) {
    return {
      id: Date.now() + Math.random(),
      number,
      text: '',
      options: ['', '', '', ''],
      answer: ''
    };
  }

  const [questions, setQuestions] = useState(() => {
    // Use function initializer to avoid issues
    if (initialQuestions.length > 0) {
      return initialQuestions;
    }
    return [createEmptyQuestion(1)];
  });
  const [error, setError] = useState(null);
  const isInitialMount = useRef(true);

  // Helper function to notify parent
  const notifyParent = (updatedQuestions) => {
    if (!onQuestionsCreated) {
      console.error('❌ onQuestionsCreated callback is not defined!');
      return;
    }
    
    const formattedQuestions = updatedQuestions.map(q => {
      const filledOptions = q.options.filter(opt => opt.trim());
      const answerLetter = q.answer.trim().toUpperCase();
      const answerIndex = answerLetter.charCodeAt(0) - 65;
      const answerText = filledOptions[answerIndex] || '';
      
      return {
        text: q.text.trim(),
        options: filledOptions,
        answer: answerText,
        // Keep original data for editing
        _original: {
          id: q.id,
          number: q.number,
          text: q.text,
          options: q.options,
          answer: q.answer
        }
      };
    });
    
    console.log('📤 ManualQuizCreator: Notifying parent with', formattedQuestions.length, 'questions');
    console.log('📤 Callback type:', typeof onQuestionsCreated);
    onQuestionsCreated(formattedQuestions);
    console.log('✅ Callback executed');
  };

  // Only notify parent on initial mount (to sync restored draft)
  useEffect(() => {
    if (isInitialMount.current && initialQuestions.length > 0) {
      console.log('🔄 ManualQuizCreator: Initial mount with', initialQuestions.length, 'questions');
      notifyParent(questions);
      isInitialMount.current = false;
    }
  }, []); // Run once on mount


  const addQuestion = () => {
    const updated = [...questions, createEmptyQuestion(questions.length + 1)];
    setQuestions(updated);
    notifyParent(updated);
  };

  const removeQuestion = (id) => {
    if (questions.length > 1) {
      const filtered = questions.filter(q => q.id !== id);
      // Renumber questions
      const renumbered = filtered.map((q, idx) => ({ ...q, number: idx + 1 }));
      setQuestions(renumbered);
      notifyParent(renumbered);
    }
  };

  const moveQuestion = (index, direction) => {
    const newQuestions = [...questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < questions.length) {
      [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
      // Renumber after swap
      const renumbered = newQuestions.map((q, idx) => ({ ...q, number: idx + 1 }));
      setQuestions(renumbered);
      notifyParent(renumbered);
    }
  };

  const updateQuestion = (id, field, value) => {
    const updated = questions.map(q => 
      q.id === id ? { ...q, [field]: value } : q
    );
    setQuestions(updated);
    notifyParent(updated);
  };

  const updateOption = (questionId, optionIndex, value) => {
    const updated = questions.map(q => {
      if (q.id === questionId) {
        const newOptions = [...q.options];
        newOptions[optionIndex] = value;
        return { ...q, options: newOptions };
      }
      return q;
    });
    setQuestions(updated);
    notifyParent(updated);
  };

  const addOption = (questionId) => {
    const updated = questions.map(q => {
      if (q.id === questionId && q.options.length < 6) {
        return { ...q, options: [...q.options, ''] };
      }
      return q;
    });
    setQuestions(updated);
    notifyParent(updated);
  };

  const removeOption = (questionId, optionIndex) => {
    const updated = questions.map(q => {
      if (q.id === questionId && q.options.length > 2) {
        const newOptions = q.options.filter((_, i) => i !== optionIndex);
        return { ...q, options: newOptions };
      }
      return q;
    });
    setQuestions(updated);
    notifyParent(updated);
  };

  const duplicateQuestion = (id) => {
    const questionToDuplicate = questions.find(q => q.id === id);
    if (questionToDuplicate) {
      const newQuestion = {
        ...questionToDuplicate,
        id: Date.now() + Math.random(),
        number: questions.length + 1
      };
      const updated = [...questions, newQuestion];
      setQuestions(updated);
      notifyParent(updated);
    }
  };

  const validateQuestions = () => {
    setError(null);

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      
      if (!q.text.trim()) {
        setError(`Question ${i + 1}: Question text is required`);
        return false;
      }

      const filledOptions = q.options.filter(opt => opt.trim());
      if (filledOptions.length < 2) {
        setError(`Question ${i + 1}: At least 2 options are required`);
        return false;
      }

      if (!q.answer.trim()) {
        setError(`Question ${i + 1}: Correct answer is required`);
        return false;
      }

      const answerLetter = q.answer.trim().toUpperCase();
      const answerIndex = answerLetter.charCodeAt(0) - 65;
      if (answerIndex < 0 || answerIndex >= filledOptions.length) {
        setError(`Question ${i + 1}: Answer must be between A and ${String.fromCharCode(65 + filledOptions.length - 1)}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateQuestions()) {
      return;
    }

    // Format questions for parent component
    const formattedQuestions = questions.map(q => {
      const filledOptions = q.options.filter(opt => opt.trim());
      const answerLetter = q.answer.trim().toUpperCase();
      const answerIndex = answerLetter.charCodeAt(0) - 65; // A=0, B=1, etc.
      const answerText = filledOptions[answerIndex]; // Get the actual text of the correct option
      
      return {
        text: q.text.trim(),
        options: filledOptions,
        answer: answerText // Use the actual option text, not the letter
      };
    });

    onQuestionsCreated(formattedQuestions);
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <span className="text-xl mr-2">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Questions ({questions.length})
        </h3>
        <button
          type="button"
          onClick={addQuestion}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          <span>Add Question</span>
        </button>
      </div>

      <div className="space-y-4">
        {questions.map((question, qIndex) => (
          <div
            key={question.id}
            className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors"
          >
            {/* Question Header */}
            <div className="flex justify-between items-start mb-4">
              <h4 className="text-lg font-semibold text-gray-900">
                Question {question.number}
              </h4>
              <div className="flex space-x-2">
                {/* Move Up/Down */}
                {qIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => moveQuestion(qIndex, 'up')}
                    className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Move up"
                  >
                    <ArrowUpIcon className="w-4 h-4" />
                  </button>
                )}
                {qIndex < questions.length - 1 && (
                  <button
                    type="button"
                    onClick={() => moveQuestion(qIndex, 'down')}
                    className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Move down"
                  >
                    <ArrowDownIcon className="w-4 h-4" />
                  </button>
                )}
                {/* Duplicate */}
                <button
                  type="button"
                  onClick={() => duplicateQuestion(question.id)}
                  className="p-1 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                  title="Duplicate"
                >
                  📋
                </button>
                {/* Delete */}
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeQuestion(question.id)}
                    className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Question Text */}
            <div className="mb-4">
              <label htmlFor={`question-text-${question.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                Question Text *
              </label>
              <textarea
                id={`question-text-${question.id}`}
                value={question.text}
                onChange={(e) => updateQuestion(question.id, 'text', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your question here..."
              />
            </div>

            {/* Options */}
            <div className="mb-4">
              <label htmlFor={`option-0-${question.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                Answer Options * (minimum 2)
              </label>
              <div className="space-y-2">
                {question.options.map((option, optIndex) => (
                  <div key={optIndex} className="flex gap-2">
                    <div className="flex-shrink-0 w-8 h-10 flex items-center justify-center bg-gray-100 rounded font-semibold text-gray-700">
                      {String.fromCharCode(65 + optIndex)}
                    </div>
                    <input
                      id={`option-${optIndex}-${question.id}`}
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(question.id, optIndex, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                    />
                    {question.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(question.id, optIndex)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {question.options.length < 6 && (
                <button
                  type="button"
                  onClick={() => addOption(question.id)}
                  className="mt-2 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  + Add Option
                </button>
              )}
            </div>

            {/* Correct Answer */}
            <div>
              <label htmlFor={`answer-${question.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                Correct Answer *
              </label>
              <input
                id={`answer-${question.id}`}
                type="text"
                value={question.answer}
                onChange={(e) => updateQuestion(question.id, 'answer', e.target.value.toUpperCase())}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="A, B, C..."
                maxLength={1}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Action Button */}
      <div className="flex justify-end pt-4 border-t">
        <button
          type="button"
          onClick={handleSubmit}
          className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
        >
          <CheckCircleIcon className="w-5 h-5" />
          <span>Use These Questions ({questions.length})</span>
        </button>
      </div>
    </div>
  );
};

ManualQuizCreator.propTypes = {
  onQuestionsCreated: PropTypes.func.isRequired,
  initialQuestions: PropTypes.array
};

export default ManualQuizCreator;
